// Screen 7 (spec 13 §Screens; design spec §9): manual numbers of the company (the gate numbers
// on `htva`) or of one seat, managers and operators; a `kpi` screen. "Reporting for" picks the
// subject (KPI-FIX #6): the company, an explicit choice and the default, or an open seat
// ("Senior Recruiter · Priya Shah"), whose own manual metrics are then offered. Spec 13 writes
// observations as `company#manager` (`seat#can_edit`), so every open seat is listed to the managers
// and operators who reach this screen. Metric, period (the metric's cadence: a day, week or
// month input, or the recent quarters or years, KPI-FIX #2), value and note → `POST
// /observations` (a second value for a period supersedes the live one). The history marks each cell's live value the way the API reads it
// (domain-kpi `liveOf`: a removal cancels the value under it) and offers "Remove" on a live
// value (`DELETE /observations/{id}`, a tombstone: the previous value is live again; coverage
// row 51). Derived metrics (formula, events, the gate fan-out) are listed read-only. Calls on
// load: the company (its currency), the metrics, the company's observations and the org chart
// (the seat picker; the Cadence sidebar reads it too). A picked seat adds its metric set and its
// observations.
import { CADENCE_TO_PERIOD } from "@companyos/domain-kpi";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  PageHeader,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalculatorIcon, HashIcon, HistoryIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef, Observation } from "../../api/types";
import { orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { formatDateTime, formatMetricValue, seatHolderLabel } from "../format";
import { holdersOf } from "../home/home-queries";
import { PagedPicker } from "../paged-picker";
import {
  companyObservationsQuery,
  companyQuery,
  metricsQuery,
  seatMetricSetQuery,
  seatObservationsQuery,
} from "../queries";
import { Loading, LoadMore } from "../screen-parts";
import { toStoredValue, UNIT_HINT } from "../structure/labels";
import { ConfirmButton, ErrorNotice, FormError, ReadOnlyNotice } from "../structure/parts";
import { useEveryPage } from "../use-every-page";
import { useFeature } from "../use-feature";
import {
  type CompanyCalendar,
  companyPeriodNow,
  type EntryPeriodKind,
  recentPeriods,
} from "./calendar";

/**
 * A gate the gate tracker computes, never entered (13 §Gate tracker): its metric's gate spec
 * (PLATFORM-OPERATOR, KPI data) measures a ratio or the client fan-out.
 */
const isComputedGate = (m: MetricDef) =>
  m.gate?.measure === "ratio" || m.gate?.measure === "fanout";

const isManual = (m: MetricDef) =>
  (m.source === "manual" || m.source === "import") && !isComputedGate(m) && !m.archived_at;

type PeriodKind = EntryPeriodKind;

/** The period type a metric's values take (the API's `CADENCE_TO_PERIOD`); null: no cadence. */
export function periodOf(m: Pick<MetricDef, "cadence">): PeriodKind | null {
  return m.cadence ? CADENCE_TO_PERIOD[m.cadence] : null;
}

const PERIOD_PATTERN: Record<PeriodKind, RegExp> = {
  day: /^\d{4}-\d{2}-\d{2}$/,
  week: /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/,
  month: /^\d{4}-(0[1-9]|1[0-2])$/,
  quarter: /^\d{4}-Q[1-4]$/,
  year: /^\d{4}$/,
};

const PERIOD_LABEL: Record<PeriodKind, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

/** "company" or a seat id: whose number this is. */
type Subject = string;
const COMPANY: Subject = "company";

export function NumbersForm({ api, company }: { api: Api; company: CompanyAccess }) {
  const detail = useQuery(companyQuery(api, company.id));
  const metrics = useInfiniteQuery(metricsQuery(api, company.id));
  // WAVE12-FIX: the Metric picker offers every metric (a new one sits past the first page's 50).
  useEveryPage(metrics);
  const chart = useQuery(orgChartQuery(api, company.id));
  const [subject, setSubject] = useState<Subject>(COMPANY);
  const seatId = subject === COMPANY ? null : subject;
  const seatSet = useQuery({
    ...seatMetricSetQuery(api, company.id, seatId ?? ""),
    enabled: seatId !== null,
  });
  const companyRows = useInfiniteQuery({
    ...companyObservationsQuery(api, company.id),
    enabled: seatId === null,
  });
  const seatRows = useInfiniteQuery({
    ...seatObservationsQuery(api, company.id, seatId ?? ""),
    enabled: seatId !== null,
  });
  const rows = seatId ? seatRows : companyRows;
  const currency = detail.data?.reporting_currency ?? "USD";
  const all = metrics.data?.pages.flatMap((p) => p.data) ?? [];
  const manual = all.filter(isManual);
  const holders = holdersOf(chart.data);
  const seats = (chart.data?.seats ?? [])
    .filter((x) => x.status !== "closed")
    .map((x) => ({ id: x.id, label: seatHolderLabel(x.title, holders.get(x.id)?.[0]?.name) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const subjectLabel = seatId
    ? (seats.find((x) => x.id === seatId)?.label ?? "the seat")
    : company.name;
  // A seat is offered the manual metrics of its own set (every tracking mode).
  const onSeat = seatSet.data ? new Set(seatSet.data.data.map((l) => l.metric_def_id)) : null;
  const offered = seatId ? manual.filter((m) => onSeat?.has(m.id)) : manual;
  const picker = (
    <Field>
      <FieldLabel htmlFor="numbers-subject">Reporting for</FieldLabel>
      <NativeSelect
        id="numbers-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      >
        <NativeSelectOption value={COMPANY}>Company · {company.name}</NativeSelectOption>
        {seats.map((x) => (
          <NativeSelectOption key={x.id} value={x.id}>
            {x.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );
  const derived = all.filter((m) => !isManual(m) && !m.archived_at);
  const kpi = useFeature("kpi");
  return (
    <>
      <PageHeader
        eyebrow="Setup · Data entry"
        title="Enter numbers"
        subtitle={`Manual numbers of ${company.name} or of one of its seats, one value per metric and period. A second value for a period replaces the first; removing a value brings the one before it back.`}
      />
      <div className="flex min-w-0 max-w-[1240px] flex-col gap-6">
        {metrics.isPending || detail.isPending ? (
          <Loading label="Loading metrics" rows={2} />
        ) : metrics.isError ? (
          <ErrorNotice what="Metrics" error={metrics.error} />
        ) : kpi.readOnly ? (
          <ReadOnlyNotice what="values" />
        ) : !manual.length ? (
          // Final review #3: while a cursor remains, later pages may hold manual metrics, so
          // paging stays available instead of a final "none".
          metrics.hasNextPage ? (
            <div data-slot="numbers-more" className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                No manual metric among the {all.length} loaded so far.
              </p>
              <LoadMore
                what="metrics"
                hasMore
                loading={metrics.isFetchingNextPage}
                onMore={() => void metrics.fetchNextPage()}
              />
            </div>
          ) : (
            <EmptyState
              variant="card"
              icon={HashIcon}
              title="No manual metrics"
              description="Every metric of this company is computed; nothing is entered by hand. Define an entered metric under Setup › Metrics & targets."
            />
          )
        ) : (
          <NumberEntry
            key={subject}
            api={api}
            co={company.id}
            subject={seatId}
            subjectLabel={subjectLabel}
            picker={picker}
            loadingSet={seatId !== null && seatSet.isPending}
            metrics={offered}
            currency={currency}
            calendar={detail.data?.calendar_config ?? company.calendar}
            more={{
              hasMore: metrics.hasNextPage,
              loading: metrics.isFetchingNextPage,
              onMore: () => void metrics.fetchNextPage(),
            }}
          />
        )}
        <SettingsSection
          id="numbers-recent"
          icon={HistoryIcon}
          title="Entered values"
          description={`${subjectLabel}: newest period first. Each cell's live value is the one the scorecard reads.`}
        >
          {rows.isPending ? (
            <Loading label="Loading entered values" rows={3} />
          ) : rows.isError ? (
            <ErrorNotice what="Entered values" error={rows.error} />
          ) : (
            <ObservationsTable
              api={api}
              co={company.id}
              subjectLabel={seatId ? subjectLabel : "the company"}
              rows={rows.data.pages.flatMap((p) => p.data)}
              metrics={all}
              currency={currency}
              canRemove={!kpi.readOnly}
            />
          )}
          <LoadMore
            what="values"
            hasMore={rows.hasNextPage}
            loading={rows.isFetchingNextPage}
            onMore={() => void rows.fetchNextPage()}
          />
        </SettingsSection>
        {derived.length ? (
          <SettingsSection
            id="numbers-derived"
            icon={CalculatorIcon}
            title="Derived metrics"
            description="Computed from other numbers or events; read-only here."
          >
            <Table data-slot="derived-metrics">
              <TableCaption>Metrics that are not entered by hand.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Metric</TableHead>
                  <TableHead scope="col">Computed from</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {derived.map((m) => (
                  <TableRow key={m.id} data-metric={m.key}>
                    <TableHead scope="row">
                      <span className="flex flex-wrap items-center gap-1.5">
                        {m.name}
                        <Badge variant="neutral">derived</Badge>
                      </span>
                    </TableHead>
                    <TableCell className="text-muted-foreground">
                      {m.formula ?? (m.source === "event" ? "Events (ingest)" : "Gate tracker")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SettingsSection>
        ) : null}
      </div>
    </>
  );
}

function NumberEntry({
  api,
  co,
  subject,
  subjectLabel,
  picker,
  loadingSet,
  metrics,
  currency,
  calendar,
  more,
}: {
  api: Api;
  co: string;
  /** The seat the value is for, or null for the company. */
  subject: string | null;
  subjectLabel: string;
  picker: ReactNode;
  loadingSet: boolean;
  metrics: MetricDef[];
  currency: string;
  calendar: CompanyCalendar;
  more: { hasMore: boolean; loading: boolean; onMore: () => void };
}) {
  const qc = useQueryClient();
  const [metricId, setMetricId] = useState(metrics[0]?.id ?? "");
  const metric = metrics.find((m) => m.id === metricId) ?? metrics[0];
  const kind = metric ? periodOf(metric) : null;
  const [periods, setPeriods] = useState<Partial<Record<PeriodKind, string>>>({});
  // The default is the company's current period, in its calendar (review #7).
  const period = kind ? (periods[kind] ?? companyPeriodNow(calendar, kind)) : "";
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const stored = metric ? toStoredValue(value, metric.unit, currency) : null;
  const validPeriod = kind !== null && PERIOD_PATTERN[kind].test(period);
  const save = useMutation({
    mutationFn: () =>
      api.client.observations.create({
        params: { co },
        body: {
          metric_def_id: metric?.id ?? "",
          subject_kind: subject ? "seat" : "company",
          subject_id: subject ?? co,
          period_type: kind ?? "day",
          period_key: period,
          value: String(stored),
          note: note.trim() ? note.trim() : null,
        },
      }),
    onSuccess: (row) => {
      setStatus(`Saved ${metric?.name ?? "the value"} for ${row.period_key}.`);
      setValue("");
      setNote("");
      // The entered values of either subject, and every scorecard that reads them.
      void qc.invalidateQueries({ queryKey: ["company", co, "observations"] });
      void qc.invalidateQueries({ queryKey: ["company", co, "scorecard"] });
      if (subject) void qc.invalidateQueries({ queryKey: ["company", co, "seat", subject] });
      void qc.invalidateQueries({ queryKey: ["operator", "gate"] });
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (stored !== null && validPeriod) save.mutate();
  };
  const hint = metric ? UNIT_HINT[metric.unit] : undefined;
  return (
    <SettingsSection
      icon={HashIcon}
      title="Add a value"
      description="Pick whose number it is, the metric, its period and the value."
    >
      <form onSubmit={onSubmit} aria-label="Add a value" className="flex flex-col gap-5">
        <div className="grid gap-5 sm:grid-cols-3">{picker}</div>
        {loadingSet ? (
          <Loading label="Loading the seat's metrics" rows={1} />
        ) : !metric ? (
          <Notice tone="neutral" data-slot="numbers-no-seat-metric">
            {subjectLabel} has no metric entered by hand. Add one to the seat under Setup › Metrics
            &amp; targets › Seat weights, or pick another seat.
          </Notice>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-3">
              <PagedPicker
                id="numbers-metric"
                label="Metric"
                value={metric.id}
                onChange={setMetricId}
                options={metrics.map((m) => ({ id: m.id, label: m.name }))}
                {...more}
              />
              {kind === null ? (
                <Notice tone="warning" data-slot="numbers-no-cadence">
                  {metric.name} has no cadence, so it takes no periods. Give it one under Setup ›
                  Metrics &amp; targets.
                </Notice>
              ) : (
                <PeriodField
                  kind={kind}
                  period={period}
                  valid={validPeriod}
                  calendar={calendar}
                  onChange={(v) => setPeriods((p) => ({ ...p, [kind]: v }))}
                />
              )}
              <Field>
                <FieldLabel htmlFor="numbers-value">Value</FieldLabel>
                <Input
                  id="numbers-value"
                  type="number"
                  step="any"
                  required
                  value={value}
                  aria-describedby={hint ? "numbers-value-hint" : undefined}
                  onChange={(e) => setValue(e.target.value)}
                />
                {hint ? <FieldDescription id="numbers-value-hint">{hint}</FieldDescription> : null}
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="numbers-note">Note</FieldLabel>
              <Textarea
                id="numbers-note"
                rows={2}
                value={note}
                aria-describedby="numbers-note-hint"
                onChange={(e) => setNote(e.target.value)}
              />
              <FieldDescription id="numbers-note-hint">
                {metric.key === "gate.pitch_declined"
                  ? "Each “no” reason, separated by “;”."
                  : "Optional context for this value."}
              </FieldDescription>
            </Field>
            <FormError what="Saving the value" error={save.error} />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="submit"
                size="lg"
                disabled={stored === null || !validPeriod || save.isPending}
              >
                {save.isPending ? "Saving…" : "Save value"}
              </Button>
              <span role="status" className="text-sm text-muted-foreground">
                {status}
              </span>
            </div>
          </>
        )}
      </form>
    </SettingsSection>
  );
}

/**
 * The period of a value: a native day, week or month input, or for a quarterly or yearly metric
 * (no native input) the company's recent quarters or years, current first (KPI-FIX #2).
 */
function PeriodField({
  kind,
  period,
  valid,
  calendar,
  onChange,
}: {
  kind: PeriodKind;
  period: string;
  valid: boolean;
  calendar: CompanyCalendar;
  onChange: (value: string) => void;
}) {
  if (kind === "quarter" || kind === "year")
    return (
      <Field>
        <FieldLabel htmlFor="numbers-period">{PERIOD_LABEL[kind]}</FieldLabel>
        <NativeSelect id="numbers-period" value={period} onChange={(e) => onChange(e.target.value)}>
          {recentPeriods(calendar, kind, kind === "quarter" ? 8 : 5).map((p) => (
            <NativeSelectOption key={p.key} value={p.key}>
              {p.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
    );
  return (
    <Field>
      <FieldLabel htmlFor="numbers-period">{PERIOD_LABEL[kind]}</FieldLabel>
      <Input
        id="numbers-period"
        type={kind === "day" ? "date" : kind}
        value={period}
        aria-invalid={valid ? undefined : true}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

const cellKey = (r: Observation) =>
  `${r.metric_def_id}|${r.subject_kind}|${r.subject_id}|${r.period_type}|${r.period_key}`;

/**
 * Each row's standing in its cell, newest first (domain-kpi `liveOf`): a tombstone cancels the
 * next older value; the first value left standing is live; values under a newer live value are
 * superseded; cancelled values are removed.
 */
export function cellStanding(rows: Observation[]): Map<string, "live" | "superseded" | "removed"> {
  const out = new Map<string, "live" | "superseded" | "removed">();
  const cells = new Map<string, Observation[]>();
  for (const r of rows) cells.set(cellKey(r), [...(cells.get(cellKey(r)) ?? []), r]);
  for (const chain of cells.values()) {
    chain.sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
    let pending = 0;
    let liveFound = false;
    for (const r of chain) {
      if (r.tombstone) {
        pending += 1;
        out.set(r.id, "removed");
      } else if (pending > 0) {
        pending -= 1;
        out.set(r.id, "removed");
      } else if (!liveFound) {
        liveFound = true;
        out.set(r.id, "live");
      } else out.set(r.id, "superseded");
    }
  }
  return out;
}

function ObservationsTable({
  api,
  co,
  subjectLabel,
  rows,
  metrics,
  currency,
  canRemove,
}: {
  api: Api;
  co: string;
  subjectLabel: string;
  rows: Observation[];
  metrics: MetricDef[];
  currency: string;
  canRemove: boolean;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => api.client.observations.delete({ params: { co, id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["company", co, "observations"] });
      void qc.invalidateQueries({ queryKey: ["company", co, "scorecard"] });
      void qc.invalidateQueries({ queryKey: ["operator", "gate"] });
    },
  });
  if (!rows.length)
    return (
      <EmptyState
        variant="compact"
        align="start"
        title="Nothing entered yet"
        description="Values you save show up here."
      />
    );
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const standing = cellStanding(rows);
  return (
    <Table data-slot="observations">
      <TableCaption>Values entered for {subjectLabel}.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Metric</TableHead>
          <TableHead scope="col">Period</TableHead>
          <TableHead scope="col" numeric>
            Value
          </TableHead>
          <TableHead scope="col">Note</TableHead>
          <TableHead scope="col">Entered</TableHead>
          {canRemove ? (
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const m = byId.get(r.metric_def_id);
          const state = standing.get(r.id) ?? "live";
          const shown = r.tombstone
            ? "Removed"
            : formatMetricValue(
                r.value === null ? null : Number(r.value),
                m?.unit ?? "number",
                currency,
              );
          return (
            <TableRow
              key={r.id}
              data-metric={m?.key}
              data-superseded={state === "superseded" || undefined}
              data-standing={state}
            >
              <TableHead scope="row">
                <span className="flex flex-wrap items-center gap-1.5">
                  {m?.name ?? "Metric"}
                  {r.tombstone ? (
                    <Badge variant="outline">Removal</Badge>
                  ) : state === "superseded" ? (
                    <Badge variant="outline">Superseded</Badge>
                  ) : state === "removed" ? (
                    <Badge variant="warning">Removed</Badge>
                  ) : null}
                </span>
              </TableHead>
              <TableCell className="font-mono text-xs">{r.period_key}</TableCell>
              <TableCell numeric>{shown}</TableCell>
              <TableCell className="max-w-72 text-muted-foreground">{r.note ?? "—"}</TableCell>
              <TableCell className="text-muted-foreground">
                {formatDateTime(r.created_at)}
              </TableCell>
              {canRemove ? (
                <TableCell className="text-right">
                  {state === "live" && !r.tombstone && m && isManual(m) ? (
                    <ConfirmButton
                      label="Remove"
                      variant="ghost"
                      srLabel={`${m.name} for ${r.period_key}`}
                      title={`Remove ${m.name} for ${r.period_key}?`}
                      description={`${shown} is withdrawn and the value entered before it, if any, applies again. The row stays in the history.`}
                      confirmLabel="Remove value"
                      pending={remove.isPending}
                      onConfirm={() => remove.mutateAsync(r.id)}
                    />
                  ) : null}
                </TableCell>
              ) : null}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
